import { Link } from 'react-router-dom';
import type { SignalDto, SourcesOverviewDto } from '@intelimove/contracts';
import { useAuth } from '../auth/AuthContext';
import { Icon, type IconName } from '../components/Icon';
import { AsyncView } from '../components/States';
import { PageHead } from '../components/Widgets';
import { CATEGORY_LABEL, relativeTime, signalsAdapter } from '../data/adapters';
import { intelApi } from '../data/intel';
import { useResource } from '../hooks/useResource';
import { CommercialMapSummary } from '../components/OpenData';
import { decisionsApi } from '../data/executive';
import { DecisionMetrics } from './Decisions';
import { ScenarioMetrics } from './Scenarios';
import { scenariosApi } from '../data/scenarios';
import { learningApi } from '../data/learning';
import { LearningMetrics } from './Learning';
import { JourneysOverviewCard } from './Journeys';

const ICON: Record<string, IconName> = { expansao: 'expansion', investimento: 'moves', tecnologia: 'accounts', lideranca: 'person', contratacao: 'person', concorrencia: 'signals', outro: 'signals' };
const AMBER = new Set(['expansao', 'lideranca', 'concorrencia']);

function greeting(d = new Date()) {
  const h = d.getHours();
  return h < 12 ? 'Bom dia' : h < 18 ? 'Boa tarde' : 'Boa noite';
}

function AttentionList() {
  const { state, reload } = useResource((s) => signalsAdapter.list({ limit: 50 }, s).then((p) => [...p.items].sort((a, b) => b.score - a.score).slice(0, 4)), [], (d: SignalDto[]) => d.length === 0);
  return (
    <AsyncView state={state} onRetry={reload} empty={<p className="card-sub">Nenhum sinal verificável foi encontrado ainda. Ative fontes recomendadas em <Link to="/fontes?visao=recomendadas">Fontes</Link>.</p>}>
      {(items) => (
        <div className="attention-list">
          {items.map((s) => (
            <Link to={`/sinais?sinal=${s.id}`} className="attention" key={s.id}>
              <div className={`signal-icon ${AMBER.has(s.category) ? 'amber' : ''}`}><Icon name={ICON[s.category] ?? 'signals'} /></div>
              <div><b>{s.title}</b><span>{CATEGORY_LABEL[s.category]} · {s.accountName} · {s.source.name}{s.publication.domain ? ` (${s.publication.domain})` : ''} · {relativeTime(s.detectedAt)}</span></div>
              <div className={`score ${s.score >= 85 && AMBER.has(s.category) ? 'hot' : ''}`} aria-label={`Score ${s.score}`}>{s.score}</div>
            </Link>
          ))}
        </div>
      )}
    </AsyncView>
  );
}

const KIND_TAG: Record<string, string> = { EMPRESA: 'Empresa', PESSOA: 'Pessoa', PERSONA: 'Persona', SINAL: 'Sinal', SEGMENTO: 'Segmento', REGIAO: 'Região', NICHO: 'Nicho' };

/** IM-EXT-003: as quatro perguntas comerciais com dados reais — resposta, itens, evidências, confiança, pendências e data. */
function FourAnswers() {
  const { state, reload } = useResource((s) => intelApi.answers(s), []);
  return (
    <AsyncView state={state} onRetry={reload} empty={null}>
      {(d) => (
        <div className="answers-grid">
          {d.answers.map((a: any) => (  
            <article className="card answer-card" key={a.key}>
              <h2>{a.question}</h2>
              {a.answer && <p>{a.answer}</p>}
              {a.items.length > 0 && <ul className="plain-list">{a.items.slice(0, 5).map((i: any, n: number) => <li key={n}><span className="tag mint">{KIND_TAG[i.kind] ?? i.kind}</span> {i.id ? <Link to={`/empresas-alvo/${i.id}`}>{i.name}</Link> : <b>{i.name}</b>} <span className="cell-sub">{i.detail}</span></li>)}</ul>} { }
              {a.pending.length > 0 && <p className="notice-inline">{a.pending.join(' · ')}</p>}
              <p className="cell-sub">{a.evidenceCount} evidência(s){a.confidence ? ` · confiança ${a.confidence.toLowerCase()}` : ''}{a.updatedAt ? ` · atualizado ${relativeTime(a.updatedAt)}` : ''}</p>
            </article>
          ))}
        </div>
      )}
    </AsyncView>
  );
}

/** IM-EXT-008: funil empresa-alvo → revisão → pessoas, com contagens reais do tenant. */
function Pipeline() {
  const { state, reload } = useResource((s) => intelApi.pipelineSummary(s), []);
  return (
    <AsyncView state={state} onRetry={reload} empty={null}>
      {(p) => (
        <article className="card" style={{ marginBottom: 18 }}>
          <div className="card-head"><div><h2>Funil de empresas-alvo</h2><div className="card-sub">Da pesquisa à revisão e às pessoas — números atuais</div></div><Link className="link-btn" to="/empresas-alvo">Abrir</Link></div>
          <div className="metric-grid">
            <article className="metric"><small>Empresas-alvo</small><strong>{p.targets}</strong><span className="trend">{p.targetsResearching} em pesquisa{p.targetsAttention ? ` · ${p.targetsAttention} com falha ou parcial` : ''}</span></article>
            <article className="metric"><small>Aguardando revisão</small><strong>{p.targetsReview}</strong><span className="trend">decisão humana pendente</span></article>
            <article className="metric"><small>Aprovadas</small><strong>{p.targetsApproved}</strong><span className="trend">para acompanhamento</span></article>
            <article className="metric"><small>Pessoas</small><strong>{p.people}</strong><span className="trend">{p.peopleReview} para revisar{p.peopleAttention ? ` · ${p.peopleAttention} busca(s) parcial(is) ou com falha em 7 dias` : ''}</span></article>
          </div>
        </article>
      )}
    </AsyncView>
  );
}

function Coverage() {
  const { state, reload } = useResource((s) => intelApi.coverage(s), []);
  return (
    <AsyncView state={state} onRetry={reload} empty={null}>
      {(c) => { const fs = c.sourcesSummary as SourcesOverviewDto; return (
        <>
          <div className="metric-grid">
            {/* IM-UX-003: contagens de fontes da MESMA consulta da página de Fontes; cada número abre a visão correspondente. */}
            <article className="metric"><small>Fontes ativas</small><strong><Link to="/fontes?visao=ativas">{fs.active}</Link></strong><span className="trend"><Link to="/fontes?visao=recomendadas">{fs.recommended} recomendada(s) para revisar</Link>{fs.problems ? <> · <Link to="/fontes?visao=problemas">{fs.problems} com problema</Link></> : ''}</span></article>
            <article className="metric"><small>Empresas-alvo</small><strong>{c.targets.approved + c.targets.following}</strong><span className="trend">{c.targets.following} em acompanhamento · {c.targets.review} para revisar</span></article>
            <article className="metric"><small>Pessoas</small><strong>{c.people.confirmed + c.people.following}</strong><span className="trend">{c.people.review} para revisar · {c.people.discovered} novas</span></article>
            <article className="metric"><small>Sinais em 7 dias</small><strong>{c.signals.last7}</strong><span className="trend">{c.signals.review} aguardando decisão</span></article>
          </div>
          <article className="card">
            <div className="card-head"><div><h2>Saúde da inteligência</h2><div className="card-sub">Cobertura operacional — não é indicador comercial</div></div><Link className="link-btn" to="/agendas">Agendas</Link></div>
            <dl className="kv">
              <dt>Fontes pausadas / com problema</dt><dd><Link to="/fontes?visao=pausadas">{fs.paused}</Link> / <Link to="/fontes?visao=problemas">{fs.problems}</Link></dd>
              <dt>Redes sociais registradas</dt><dd>{c.sources.social} (coleta exige conexão autorizada)</dd>
              <dt>Última coleta · próxima</dt><dd>{c.sources.last_run ? relativeTime(c.sources.last_run) : '—'} · {c.sources.next_run ? new Date(c.sources.next_run).toLocaleString('pt-BR') : '—'}</dd>
              <dt>Agendas ativas / pausadas</dt><dd>{c.schedules.active} / {c.schedules.paused}</dd>
              <dt>Análises de mercado</dt><dd>{c.market.analyses} ({c.market.running} em execução)</dd>
              <dt>Fontes recomendadas aguardando revisão</dt><dd><Link to="/fontes?visao=recomendadas">{fs.recommended}</Link></dd>
              <dt>Pesquisa por IA</dt><dd>{c.executor.available ? 'Disponível' : c.executor.message}</dd>
            </dl>
            {c.recentFailures.length > 0 && <><h3>Falhas recentes</h3><ul className="plain-list">{c.recentFailures.map((f: any, i: number) => <li key={i}><b>{f.name}</b> <span className="cell-sub">{f.last_error}</span></li>)}</ul></>} { }
          </article>
        </>
      ); }}
    </AsyncView>
  );
}

/** IM-EXT-020: decisões estratégicas — a MESMA consulta da Central; cada número abre a lista correspondente. */
function DecisionsCard() {
  const { state, reload } = useResource((s) => decisionsApi.summary(s), []);
  return (
    <AsyncView state={state} onRetry={reload} empty={null}>
      {(s) => (
        <article className="card" style={{ marginBottom: 18 }}>
          <div className="card-head"><div><h2>Decisões estratégicas</h2><div className="card-sub">Propostas do InteliMove e decisões da equipe — números da Central</div></div><Link className="link-btn" to="/decisoes">Abrir Central</Link></div>
          {s.total === 0 ? <p className="card-sub">Nenhuma decisão proposta ainda. Gere o <Link to="/parecer">Parecer executivo</Link>.</p> : <DecisionMetrics s={s} />}
        </article>
      )}
    </AsyncView>
  );
}

/** IM-EXT-021: cenários e simulações — a MESMA consulta da lista; cada número abre a visão correspondente. */
function ScenariosCard() {
  const { state, reload } = useResource((s) => scenariosApi.summary(s), []);
  return (
    <AsyncView state={state} onRetry={reload} empty={null}>
      {(s) => (
        <article className="card" style={{ marginBottom: 18 }}>
          <div className="card-head"><div><h2>Cenários e simulações</h2><div className="card-sub">Alternativas comparadas antes de decidir — números da área de Cenários</div></div><Link className="link-btn" to="/cenarios">Abrir Cenários</Link></div>
          {s.total === 0 ? <p className="card-sub">Nenhuma simulação ainda. Na <Link to="/decisoes">Central de decisões</Link>, use “Simular alternativas”.</p> : <ScenarioMetrics s={s} />}
        </article>
      )}
    </AsyncView>
  );
}

/** IM-EXT-022: resultados e aprendizado — as MESMAS regras das abas; cada número abre a visão correspondente. */
function LearningCard() {
  const { state, reload } = useResource((s) => learningApi.summary(s), []);
  return (
    <AsyncView state={state} onRetry={reload} empty={null}>
      {(s) => (
        <article className="card" style={{ marginBottom: 18 }}>
          <div className="card-head"><div><h2>Resultados e aprendizado</h2><div className="card-sub">O que os resultados ensinam — esperado × realizado, benchmarks e ajustes</div></div><Link className="link-btn" to="/aprendizado">Abrir</Link></div>
          {!s.hasReport && s.unassociated === 0 && s.staleBenchmarks === 0 ? <p className="card-sub">Nenhum resultado registrado ainda. <Link to="/aprendizado?aba=resultados">Registrar ou importar resultados</Link>.</p> : <LearningMetrics s={s} />}
        </article>
      )}
    </AsyncView>
  );
}

export function Overview() {
  const { me } = useAuth();
  const first = me?.user.name.split(' ')[0] ?? '';
  const today = new Intl.DateTimeFormat('pt-BR', { weekday: 'long', day: 'numeric', month: 'long' }).format(new Date());
  return (
    <section aria-labelledby="page-title">
      <PageHead eyebrow={today} title={`${greeting()}, ${first}.`} subtitle="Para quem vender, com quem falar, por que comprariam e quando abordar."
        actions={<><Link className="secondary btn-link" to="/empresas-alvo">Empresas-alvo</Link><Link className="cta btn-link" to="/mercado/nova">Analisar mercado</Link></>} />
      {/* IM-JRN-007 (IM-UX-108): porta de entrada das jornadas; não renderiza nada com a camada desligada. */}
      <JourneysOverviewCard />
      <CommercialMapSummary />
      <DecisionsCard />
      <ScenariosCard />
      <LearningCard />
      <Pipeline />
      <FourAnswers />
      <div className="dashboard-grid" style={{ marginTop: 18 }}>
        <div><Coverage /></div>
        <article className="card">
          <div className="card-head"><div><h2>Requer atenção</h2><div className="card-sub">Sinais priorizados por impacto e urgência</div></div><Link className="link-btn" to="/sinais">Ver tudo</Link></div>
          <AttentionList />
        </article>
      </div>
    </section>
  );
}
